<template>
  <div class="dashboard-container mt-3 pb-5">
    <!-- Header y Estado de Conexión Comet -->
    <div class="card mb-4 shadow-sm border-0 bg-white">
      <div class="card-body p-3 p-md-4">
        <div class="d-flex flex-column flex-md-row justify-content-between align-items-md-center">
          <div>
            <h3 class="mb-1 text-dark font-weight-bold d-flex align-items-center">
              <i class="bi bi-speedometer2 text-primary mr-2"></i> Dashboard en Vivo
            </h3>
            <p class="text-muted mb-0 small">
              Monitoreo y telemetría de mensajes, eventos y CTRs en tiempo real vía <strong>Comet (SSE)</strong>
            </p>
          </div>

          <div class="mt-3 mt-md-0 d-flex flex-wrap align-items-center">
            <!-- Badge de estado Comet -->
            <div class="mr-3 mb-2 mb-sm-0">
              <span v-if="cometStatus === 'connected'" class="badge badge-pill badge-light-success p-2 px-3 border border-success">
                <span class="live-dot bg-success mr-2"></span>
                <strong class="text-success">Comet En Vivo</strong>
              </span>
              <span v-else-if="cometStatus === 'connecting'" class="badge badge-pill badge-light-warning p-2 px-3 border border-warning">
                <span class="live-dot bg-warning mr-2"></span>
                <strong class="text-warning">Conectando...</strong>
              </span>
              <span v-else class="badge badge-pill badge-light-danger p-2 px-3 border border-danger">
                <span class="live-dot bg-danger mr-2"></span>
                <strong class="text-danger">Comet Pausado / Desconectado</strong>
              </span>
            </div>

            <!-- Botones de Control -->
            <div class="btn-group btn-group-sm mr-2 mb-2 mb-sm-0">
              <button
                class="btn"
                :class="isPaused ? 'btn-outline-success' : 'btn-outline-secondary'"
                @click="toggleStream"
                :title="isPaused ? 'Reanudar flujo Comet' : 'Pausar flujo Comet'"
              >
                <i :class="isPaused ? 'bi bi-play-fill mr-1' : 'bi bi-pause-fill mr-1'"></i>
                {{ isPaused ? 'Reanudar' : 'Pausar' }}
              </button>
              <button
                class="btn btn-outline-info"
                @click="fetchSnapshot"
                title="Actualizar datos inmediatos"
                :disabled="isLoading"
              >
                <i class="bi bi-arrow-clockwise mr-1" :class="{ 'fa-spin': isLoading }"></i>
                Refrescar
              </button>
              <button
                class="btn btn-outline-danger"
                @click="confirmReset"
                title="Reiniciar contadores del simulador"
              >
                <i class="bi bi-arrow-counterclockwise mr-1"></i>
                Reset
              </button>
            </div>

            <!-- Selector de intervalo de actualización -->
            <div class="input-group input-group-sm stream-speed-select mb-2 mb-sm-0" style="width: 140px;">
              <div class="input-group-prepend">
                <span class="input-group-text bg-light border-right-0 text-muted" title="Frecuencia Comet">
                  <i class="bi bi-clock-history"></i>
                </span>
              </div>
              <select
                class="form-control form-control-sm border-left-0"
                v-model.number="streamInterval"
                @change="restartStream"
                title="Intervalo del streaming Comet"
              >
                <option :value="1.0">1.0 seg</option>
                <option :value="2.0">2.0 seg</option>
                <option :value="3.0">3.0 seg</option>
                <option :value="5.0">5.0 seg</option>
              </select>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- TARJETAS PRINCIPALES: MENSAJES, EVENTOS, CTRS -->
    <div class="row">
      <!-- 1. TARJETA MENSAJES -->
      <div class="col-12 col-lg-4 mb-4">
        <kpi-card
          card-class="kpi-messages"
          icon="bi bi-chat-text-fill"
          icon-box-class="bg-light-primary text-primary"
          rate-badge-class="badge-primary"
          :rate-text="metrics.rates.mensajes_seg + ' msg/seg'"
          :total="formatNumber(metrics.mensajes.total)"
          title="Mensajes"
          :main-value="formatNumber(metrics.mensajes.enviados)"
          :trend-percent="metrics.mensajes.tasa_entrega"
          trend-class="text-success"
          stat1-label="Enviados"
          :stat1-value="formatNumber(metrics.mensajes.enviados)"
          stat1-icon="bi bi-check-circle"
          stat1-class="text-success"
          stat2-label="No Enviados"
          :stat2-value="formatNumber(metrics.mensajes.no_enviados)"
          stat2-icon="bi bi-x-circle"
          stat2-class="text-danger"
          bar1-label="Tasa de Entrega"
          :bar1-percent="metrics.mensajes.tasa_entrega"
          bar1-class="text-success"
          bar1-bar-class="bg-success"
          bar2-label="Fallo"
          :bar2-percent="metrics.mensajes.tasa_fallo"
          bar2-class="text-danger"
          bar2-bar-class="bg-danger"
        />
      </div>

      <!-- 2. TARJETA EVENTOS -->
      <div class="col-12 col-lg-4 mb-4">
        <kpi-card
          card-class="kpi-events"
          icon="bi bi-lightning-charge-fill"
          icon-box-class="bg-light-info text-info"
          rate-badge-class="badge-info"
          :rate-text="metrics.rates.eventos_seg + ' evt/seg'"
          :total="formatNumber(metrics.eventos.total)"
          title="Eventos"
          :main-value="formatNumber(metrics.eventos.enviados)"
          :trend-percent="metrics.eventos.tasa_exito"
          trend-class="text-info"
          stat1-label="Enviados"
          :stat1-value="formatNumber(metrics.eventos.enviados)"
          stat1-icon="bi bi-check-circle"
          stat1-class="text-info"
          stat2-label="No Enviados"
          :stat2-value="formatNumber(metrics.eventos.no_enviados)"
          stat2-icon="bi bi-exclamation-circle"
          stat2-class="text-warning"
          bar1-label="Tasa de Éxito"
          :bar1-percent="metrics.eventos.tasa_exito"
          bar1-class="text-info"
          bar1-bar-class="bg-info"
          bar2-label="Rechazados"
          :bar2-percent="metrics.eventos.tasa_fallo"
          bar2-class="text-warning"
          bar2-bar-class="bg-warning"
        />
      </div>

      <!-- 3. TARJETA CTRS -->
      <div class="col-12 col-lg-4 mb-4">
        <kpi-card
          card-class="kpi-ctrs"
          icon="bi bi-hdd-stack-fill"
          icon-box-class="bg-light-success text-success"
          rate-badge-class="badge-success"
          :rate-text="metrics.rates.ctrs_seg + ' ctr/seg'"
          :total="formatNumber(metrics.ctrs.total)"
          title="CTRs"
          :main-value="formatNumber(metrics.ctrs.almacenados)"
          :trend-percent="metrics.ctrs.tasa_almacenamiento"
          trend-class="text-teal"
          stat1-label="Almacenados"
          :stat1-value="formatNumber(metrics.ctrs.almacenados)"
          stat1-icon="bi bi-check-circle"
          stat1-class="text-success"
          stat2-label="No Almacenados"
          :stat2-value="formatNumber(metrics.ctrs.no_almacenados)"
          stat2-icon="bi bi-clock"
          stat2-class="text-secondary"
          bar1-label="Almacenados"
          :bar1-percent="metrics.ctrs.tasa_almacenamiento"
          bar1-class="text-success"
          bar1-bar-class="bg-success"
          bar2-label="No Almacenados"
          :bar2-percent="metrics.ctrs.tasa_no_almacenado"
          bar2-class="text-secondary"
          bar2-bar-class="bg-secondary"
        />
      </div>
    </div>

    <!-- SECCIÓN SECUNDARIA: COMPARATIVAS Y RENDIMIENTO TEMPORAL -->
    <div class="row mb-4">
      <!-- Comparativa Visual -->
      <div class="col-12 col-lg-6 mb-4 mb-lg-0">
        <div class="card h-100 shadow-sm border-0">
          <div class="card-header bg-white border-0 pt-4 pb-0 d-flex justify-content-between align-items-center">
            <h5 class="font-weight-bold mb-0 text-dark">
              <i class="bi bi-bar-chart-line mr-2 text-primary"></i> Relación de Estados
            </h5>
            <span class="badge badge-light text-muted">En vivo</span>
          </div>
          <div class="card-body">
            <div class="comparison-bar-item mb-4">
              <div class="d-flex justify-content-between align-items-center mb-1">
                <div>
                  <span class="badge badge-light-primary mr-1"><i class="bi bi-chat-text"></i></span>
                  <strong class="text-dark">Mensajes:</strong>
                  <span class="text-success ml-1">{{ formatNumber(metrics.mensajes.enviados) }} Enviados</span>
                  <span class="text-muted mx-1">vs</span>
                  <span class="text-danger">{{ formatNumber(metrics.mensajes.no_enviados) }} No Enviados</span>
                </div>
                <span class="font-weight-bold text-success">{{ metrics.mensajes.tasa_entrega }}%</span>
              </div>
              <div class="progress" style="height: 12px; border-radius: 6px;">
                <div class="progress-bar bg-success" :style="{ width: metrics.mensajes.tasa_entrega + '%' }"></div>
                <div class="progress-bar bg-danger" :style="{ width: metrics.mensajes.tasa_fallo + '%' }"></div>
              </div>
            </div>

            <div class="comparison-bar-item mb-4">
              <div class="d-flex justify-content-between align-items-center mb-1">
                <div>
                  <span class="badge badge-light-info mr-1"><i class="bi bi-lightning"></i></span>
                  <strong class="text-dark">Eventos:</strong>
                  <span class="text-info ml-1">{{ formatNumber(metrics.eventos.enviados) }} Enviados</span>
                  <span class="text-muted mx-1">vs</span>
                  <span class="text-warning">{{ formatNumber(metrics.eventos.no_enviados) }} No Enviados</span>
                </div>
                <span class="font-weight-bold text-info">{{ metrics.eventos.tasa_exito }}%</span>
              </div>
              <div class="progress" style="height: 12px; border-radius: 6px;">
                <div class="progress-bar bg-info" :style="{ width: metrics.eventos.tasa_exito + '%' }"></div>
                <div class="progress-bar bg-warning" :style="{ width: metrics.eventos.tasa_fallo + '%' }"></div>
              </div>
            </div>

            <div class="comparison-bar-item mb-2">
              <div class="d-flex justify-content-between align-items-center mb-1">
                <div>
                  <span class="badge badge-light-success mr-1"><i class="bi bi-hdd"></i></span>
                  <strong class="text-dark">CTRs:</strong>
                  <span class="text-success ml-1">{{ formatNumber(metrics.ctrs.almacenados) }} Almacenados</span>
                  <span class="text-muted mx-1">vs</span>
                  <span class="text-secondary">{{ formatNumber(metrics.ctrs.no_almacenados) }} No Almacenados</span>
                </div>
                <span class="font-weight-bold text-success">{{ metrics.ctrs.tasa_almacenamiento }}%</span>
              </div>
              <div class="progress" style="height: 12px; border-radius: 6px;">
                <div class="progress-bar bg-success" :style="{ width: metrics.ctrs.tasa_almacenamiento + '%' }"></div>
                <div class="progress-bar bg-secondary" :style="{ width: metrics.ctrs.tasa_no_almacenado + '%' }"></div>
              </div>
            </div>

            <div class="mt-4 pt-3 border-top text-muted small d-flex justify-content-between align-items-center">
              <span><i class="bi bi-info-circle mr-1"></i> Actualizado en cada pulso de transmisión Comet</span>
              <span>Hora: <strong>{{ metrics.hora || '--:--:--' }}</strong></span>
            </div>
          </div>
        </div>
      </div>

      <!-- Historial de Tasas en Tiempo Real (Sparkline SVG) -->
      <div class="col-12 col-lg-6">
        <div class="card h-100 shadow-sm border-0">
          <div class="card-header bg-white border-0 pt-4 pb-0 d-flex justify-content-between align-items-center">
            <h5 class="font-weight-bold mb-0 text-dark">
              <i class="bi bi-activity mr-2 text-danger"></i> Velocidad de Procesamiento (Pulsos)
            </h5>
            <span class="badge badge-pill badge-primary">Últimos {{ rateHistory.length }} ticks</span>
          </div>
          <div class="card-body d-flex flex-column justify-content-between">
            <div class="sparkline-wrapper my-2" style="height: 130px; position: relative;">
              <!-- SVG Sparkline interactivo sin dependencias -->
              <svg viewBox="0 0 400 120" preserveAspectRatio="none" class="w-100 h-100">
                <defs>
                  <linearGradient id="gradMsg" x1="0%" y1="0%" x2="0%" y2="100%">
                    <stop offset="0%" stop-color="#007bff" stop-opacity="0.3"/>
                    <stop offset="100%" stop-color="#007bff" stop-opacity="0.0"/>
                  </linearGradient>
                </defs>
                <!-- Línea de base -->
                <line x1="0" y1="110" x2="400" y2="110" stroke="#e9ecef" stroke-width="1" stroke-dasharray="4"/>
                <line x1="0" y1="60" x2="400" y2="60" stroke="#e9ecef" stroke-width="1" stroke-dasharray="4"/>
                <!-- Path de área de mensajes -->
                <path :d="svgMsgArea" fill="url(#gradMsg)" />
                <!-- Líneas de Sparklines -->
                <path :d="svgMsgPath" fill="none" stroke="#007bff" stroke-width="2.5" stroke-linecap="round" />
                <path :d="svgCtrPath" fill="none" stroke="#28a745" stroke-width="2" stroke-linecap="round" />
                <path :d="svgEvtPath" fill="none" stroke="#17a2b8" stroke-width="2" stroke-linecap="round" />
              </svg>
            </div>

            <!-- Leyenda de Sparklines -->
            <div class="d-flex justify-content-around text-center border-top pt-3 mt-2">
              <div>
                <span class="legend-dot bg-primary mr-1"></span>
                <span class="small font-weight-bold">Mensajes</span>
                <div class="h6 mb-0 text-primary font-weight-bold">{{ metrics.rates.mensajes_seg }} /s</div>
              </div>
              <div>
                <span class="legend-dot bg-info mr-1"></span>
                <span class="small font-weight-bold">Eventos</span>
                <div class="h6 mb-0 text-info font-weight-bold">{{ metrics.rates.eventos_seg }} /s</div>
              </div>
              <div>
                <span class="legend-dot bg-success mr-1"></span>
                <span class="small font-weight-bold">CTRs</span>
                <div class="h6 mb-0 text-success font-weight-bold">{{ metrics.rates.ctrs_seg }} /s</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- SECCIÓN 3: DISTRIBUCIÓN POR OPERADOR -->
    <div class="card shadow-sm border-0 mb-4">
      <div class="card-header bg-white border-0 pt-4 pb-0 d-flex justify-content-between align-items-center">
        <h5 class="font-weight-bold mb-0 text-dark">
          <i class="bi bi-diagram-3 mr-2 text-primary"></i> Desglose por Operador
        </h5>
        <span class="badge badge-light text-muted">4 Operadores activos</span>
      </div>
      <div class="card-body p-0 pt-3">
        <div class="table-responsive">
          <table class="table table-hover table-striped mb-0 text-center">
            <thead class="thead-light">
              <tr>
                <th class="text-left pl-4">Operador</th>
                <th>Mensajes Enviados</th>
                <th>Mensajes No Enviados</th>
                <th>Eventos Enviados</th>
                <th>Eventos No Enviados</th>
                <th>CTRs Almacenados</th>
                <th>CTRs No Almacenados</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="op in metrics.operadores" :key="op.id">
                <td class="text-left pl-4 font-weight-bold text-dark">
                  <span class="badge badge-pill badge-secondary mr-2">{{ op.id }}</span>
                  {{ op.operador }}
                </td>
                <td class="text-success font-weight-bold">
                  {{ formatNumber(op.mensajes_enviados) }}
                </td>
                <td class="text-danger font-weight-bold">
                  {{ formatNumber(op.mensajes_no_enviados) }}
                </td>
                <td class="text-info font-weight-bold">
                  {{ formatNumber(op.eventos_enviados) }}
                </td>
                <td class="text-warning font-weight-bold">
                  {{ formatNumber(op.eventos_no_enviados) }}
                </td>
                <td class="text-success font-weight-bold">
                  {{ formatNumber(op.ctrs_almacenados) }}
                </td>
                <td class="text-muted font-weight-bold">
                  {{ formatNumber(op.ctrs_no_almacenados) }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- SECCIÓN 4: STREAM DE ACTIVIDAD COMET EN VIVO -->
    <div class="card shadow-sm border-0">
      <div class="card-header bg-white border-0 pt-4 pb-0 d-flex justify-content-between align-items-center">
        <div>
          <h5 class="font-weight-bold mb-0 text-dark">
            <i class="bi bi-broadcast mr-2 text-danger"></i> Feed de Transacciones Comet en Tiempo Real
          </h5>
          <p class="text-muted small mb-0">Flujo continuo de transacciones transmitidas a través de Server-Sent Events</p>
        </div>
        <span class="badge badge-pill badge-light border px-3 py-2 text-muted">
          <i class="bi bi-clock mr-1"></i> Transmitiendo cada {{ streamInterval }}s
        </span>
      </div>
      <div class="card-body p-0 pt-3">
        <div class="table-responsive" style="max-height: 380px; overflow-y: auto;">
          <table class="table table-hover table-sm mb-0 text-center align-middle">
            <thead class="thead-light" style="position: sticky; top: 0; z-index: 10;">
              <tr>
                <th style="width: 100px;">Hora</th>
                <th style="width: 120px;">Tipo</th>
                <th style="width: 130px;">Operador</th>
                <th style="width: 140px;">MSISDN</th>
                <th class="text-left">Detalle de Transacción</th>
                <th style="width: 140px;">Estado</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(act, idx) in metrics.recent_activities"
                :key="act.id || idx"
                :class="{ 'table-row-new': idx === 0 }"
              >
                <td class="text-muted small font-mono">{{ act.timestamp }}</td>
                <td>
                  <span
                    class="badge px-2 py-1"
                    :class="{
                      'badge-light-primary text-primary border border-primary': act.tipo === 'Mensaje',
                      'badge-light-info text-info border border-info': act.tipo === 'Evento',
                      'badge-light-success text-success border border-success': act.tipo === 'CTR'
                    }"
                  >
                    {{ act.tipo }}
                  </span>
                </td>
                <td class="font-weight-bold text-secondary small">{{ act.operador }}</td>
                <td class="font-mono text-muted small">{{ act.msisdn }}</td>
                <td class="text-left font-weight-normal text-dark">{{ act.detalle }}</td>
                <td>
                  <span
                    class="badge badge-pill px-2 py-1"
                    :class="{
                      'badge-success': act.badge === 'success',
                      'badge-danger': act.badge === 'danger',
                      'badge-info': act.badge === 'info',
                      'badge-warning': act.badge === 'warning',
                      'badge-secondary': !act.badge
                    }"
                  >
                    <i
                      class="mr-1"
                      :class="{
                        'bi bi-check': act.badge === 'success' || act.badge === 'info',
                        'bi bi-x': act.badge === 'danger',
                        'bi bi-exclamation': act.badge === 'warning'
                      }"
                    ></i>
                    {{ act.estado }}
                  </span>
                </td>
              </tr>
              <tr v-if="!metrics.recent_activities || !metrics.recent_activities.length">
                <td colspan="6" class="text-muted py-4">Esperando eventos del canal Comet...</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable no-console */
import axios from "axios";
import Swal from "sweetalert2/dist/sweetalert2.js";
import { getFullDownloadUrl } from "../../services/notificationService";
import KpiCard from "./utiles/KpiCard.vue";

export default {
  name: "Dashboard",
  components: {
    KpiCard,
  },

  data() {
    return {
      cometStatus: "connecting", // 'connected' | 'connecting' | 'disconnected'
      isPaused: false,
      isLoading: false,
      streamInterval: 2.0,
      eventSource: null,
      reconnectTimer: null,
      rateHistory: [], // Almacena los últimos 15 puntos de tasas para el sparkline
      metrics: {
        timestamp: "",
        hora: "",
        mensajes: {
          enviados: 14850,
          no_enviados: 312,
          total: 15162,
          tasa_entrega: 97.94,
          tasa_fallo: 2.06,
        },
        eventos: {
          enviados: 6420,
          no_enviados: 185,
          total: 6605,
          tasa_exito: 97.2,
          tasa_fallo: 2.8,
        },
        ctrs: {
          almacenados: 21270,
          no_almacenados: 84,
          total: 21354,
          tasa_almacenamiento: 99.61,
          tasa_no_almacenado: 0.39,
        },
        rates: {
          mensajes_seg: 18.5,
          eventos_seg: 7.8,
          ctrs_seg: 26.3,
        },
        operadores: [],
        recent_activities: [],
        uptime_seconds: 0,
      },
    };
  },

  computed: {
    svgMsgPath() {
      return this.generateSvgPath("mensajes_seg", 50);
    },
    svgMsgArea() {
      const line = this.generateSvgPath("mensajes_seg", 50);
      if (!line) return "";
      return `${line} L 400 110 L 0 110 Z`;
    },
    svgEvtPath() {
      return this.generateSvgPath("eventos_seg", 30);
    },
    svgCtrPath() {
      return this.generateSvgPath("ctrs_seg", 60);
    },
  },

  created() {
    this.initRateHistory();
  },

  mounted() {
    this.fetchSnapshot();
    this.startCometStream();
  },

  beforeDestroy() {
    this.stopCometStream();
  },

  methods: {
    formatNumber(num) {
      if (num === null || num === undefined) return "0";
      return Number(num).toLocaleString("es-PE");
    },

    initRateHistory() {
      // Inicializar historial con 12 puntos base
      this.rateHistory = [];
      for (let i = 0; i < 12; i++) {
        this.rateHistory.push({
          mensajes_seg: 15 + Math.random() * 8,
          eventos_seg: 6 + Math.random() * 4,
          ctrs_seg: 22 + Math.random() * 10,
        });
      }
    },

    generateSvgPath(metricKey, maxScale) {
      if (!this.rateHistory || this.rateHistory.length < 2) return "";
      const count = this.rateHistory.length;
      const stepX = 400 / (count - 1);
      const points = this.rateHistory.map((item, idx) => {
        const val = item[metricKey] || 0;
        const normY = 110 - Math.min(Math.max((val / maxScale) * 90, 5), 100);
        return `${(idx * stepX).toFixed(1)},${normY.toFixed(1)}`;
      });
      return `M ${points.join(" L ")}`;
    },

    startCometStream() {
      if (this.isPaused) return;

      this.stopCometStream();

      const token = localStorage.getItem("authToken");
      if (!token) {
        this.cometStatus = "disconnected";
        return;
      }

      this.cometStatus = "connecting";

      // Usar getFullDownloadUrl para resolver endpoint relativo o absoluto
      const streamUrl = getFullDownloadUrl(
        `dashboard/stream?token=${encodeURIComponent(token)}&interval=${this.streamInterval}`
      );

      try {
        this.eventSource = new EventSource(streamUrl);

        this.eventSource.addEventListener("connected", (event) => {
          this.cometStatus = "connected";
          console.log("[Dashboard Comet] Conexión establecida:", event.data);
        });

        this.eventSource.addEventListener("metrics", (event) => {
          try {
            const data = JSON.parse(event.data);
            this.handleMetricsUpdate(data);
          } catch (err) {
            console.error("[Dashboard Comet] Error parseando datos de métricas:", err);
          }
        });

        this.eventSource.onerror = (err) => {
          console.warn("[Dashboard Comet] Conexión interrumpida o reintentando:", err);
          this.cometStatus = "connecting";
          // El navegador reintenta automáticamente con EventSource
        };
      } catch (err) {
        console.error("[Dashboard Comet] Error instanciando EventSource:", err);
        this.cometStatus = "disconnected";
      }
    },

    stopCometStream() {
      if (this.eventSource) {
        this.eventSource.close();
        this.eventSource = null;
      }
      if (this.reconnectTimer) {
        clearTimeout(this.reconnectTimer);
        this.reconnectTimer = null;
      }
    },

    restartStream() {
      this.stopCometStream();
      if (!this.isPaused) {
        this.startCometStream();
      }
    },

    toggleStream() {
      this.isPaused = !this.isPaused;
      if (this.isPaused) {
        this.stopCometStream();
        this.cometStatus = "disconnected";
      } else {
        this.startCometStream();
      }
    },

    handleMetricsUpdate(data) {
      if (!data) return;
      this.cometStatus = "connected";

      // Actualizar métricas reactivas
      this.metrics = {
        ...this.metrics,
        ...data,
      };

      // Registrar punto en el historial de sparklines
      if (data.rates) {
        this.rateHistory.push({
          mensajes_seg: data.rates.mensajes_seg || 0,
          eventos_seg: data.rates.eventos_seg || 0,
          ctrs_seg: data.rates.ctrs_seg || 0,
        });
        if (this.rateHistory.length > 20) {
          this.rateHistory.shift();
        }
      }
    },

    async fetchSnapshot() {
      this.isLoading = true;
      try {
        const url = getFullDownloadUrl("dashboard/metrics");
        const token = localStorage.getItem("authToken");
        const response = await axios.get(url, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (response.data) {
          this.handleMetricsUpdate(response.data);
        }
      } catch (err) {
        console.error("[Dashboard] Error al consultar snapshot inicial:", err);
      } finally {
        this.isLoading = false;
      }
    },

    async confirmReset() {
      const result = await Swal.fire({
        title: "¿Reiniciar contadores?",
        text: "Se restablecerán los contadores simulados a sus valores base.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#007bff",
        cancelButtonColor: "#6c757d",
        confirmButtonText: "Sí, reiniciar",
        cancelButtonText: "Cancelar",
      });

      if (result.isConfirmed) {
        try {
          const url = getFullDownloadUrl("dashboard/reset");
          const token = localStorage.getItem("authToken");
          await axios.post(url, {}, {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          });
          await this.fetchSnapshot();
          this.initRateHistory();
          Swal.fire({
            icon: "success",
            title: "Contadores reiniciados",
            timer: 1500,
            showConfirmButton: false,
          });
        } catch (err) {
          console.error("[Dashboard] Error al reiniciar contadores:", err);
          Swal.fire({
            icon: "error",
            title: "Error",
            text: "No se pudieron reiniciar los contadores.",
          });
        }
      }
    },
  },
};
</script>

<style scoped>
.dashboard-container {
  max-width: 100%;
}

.kpi-card {
  border-radius: 12px;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.kpi-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.08) !important;
}

.kpi-icon-box {
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  font-size: 1.5rem;
}

.bg-light-primary {
  background-color: #e8f0fe !important;
}

.bg-light-info {
  background-color: #e3f7fc !important;
}

.bg-light-success {
  background-color: #e6f4ea !important;
}

.bg-light-warning {
  background-color: #fff8e1 !important;
}

.bg-light-danger {
  background-color: #fce8e6 !important;
}

.text-teal {
  color: #20c997 !important;
}

.letter-spacing-1 {
  letter-spacing: 1px;
}

.live-dot {
  display: inline-block;
  width: 9px;
  height: 9px;
  border-radius: 50%;
  animation: pulse-dot 1.8s infinite ease-in-out;
}

@keyframes pulse-dot {
  0% {
    transform: scale(0.9);
    opacity: 0.6;
  }
  50% {
    transform: scale(1.35);
    opacity: 1;
  }
  100% {
    transform: scale(0.9);
    opacity: 0.6;
  }
}

.legend-dot {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
}

.font-mono {
  font-family: SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
}

.table-row-new {
  animation: highlight-row 1.2s ease-out;
}

@keyframes highlight-row {
  from {
    background-color: #e8f4fd;
  }
  to {
    background-color: transparent;
  }
}
</style>
